import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, CalendarCheck } from "lucide-react";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREETING = { role: "assistant", content: "Namaste! I'm the Astrologer & Spiritual Healers assistant. Ask me about our services or hours, or I can book an appointment for you right here." };
const SUGGESTIONS = ["Book an appointment for me", "What services do you offer?", "Where are you located?"];

const visible = (text) => text.replace(/\[\[[\s\S]*$/, "").trim();

const BookingCard = ({ b }) => (
  <div data-testid="chat-booking-confirmation" className="w-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 p-4 text-sm">
    <p className="flex items-center gap-2 font-medium text-[#F3E5AB]"><CalendarCheck className="h-4 w-4" /> Booking request received</p>
    <dl className="mt-3 grid grid-cols-3 gap-y-1.5 text-slate-200">
      {[["Name", b.name], ["Phone", b.phone], ["Service", b.service], ["Date", b.date], ["Time", b.time]].map(([k, v]) => (
        <div key={k} className="contents"><dt className="text-slate-400">{k}</dt><dd className="col-span-2 break-words">{v}</dd></div>
      ))}
    </dl>
    <p className="mt-3 text-xs text-slate-300">Our team will contact you shortly to confirm.</p>
  </div>
);

const getSession = () => {
  let id = localStorage.getItem("ash_chat_session");
  if (!id) {
    id = `s_${crypto.randomUUID ? crypto.randomUUID() : Date.now() + Math.random().toString(36).slice(2)}`;
    localStorage.setItem("ash_chat_session", id);
  }
  return id;
};

const linkify = (text) =>
  text.split(/(https?:\/\/[^\s)]+|\/book\b)/g).map((part, i) =>
    /^https?:\/\//.test(part) || part === "/book" ? (
      <a key={i} href={part} target={part === "/book" ? undefined : "_blank"} rel="noopener noreferrer" className="break-all text-[#D4AF37] underline">{part === "/book" ? "Book page" : part}</a>
    ) : part
  );

export const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = text.trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    const append = (t) => setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + t };
      return copy;
    });
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSession(), message: msg }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        append(typeof err?.detail === "string" ? err.detail : "Sorry, something went wrong. Please call or WhatsApp +1 368-886-0978.");
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const data = p.replace(/^data: /, "");
          if (data === "[DONE]") continue;
          try {
            const ev = JSON.parse(data);
            if (ev.booking) setMessages((m) => [...m, { role: "booking", booking: ev.booking }]);
            else if (ev.booking_error) setMessages((m) => [...m, { role: "assistant", content: ev.booking_error }]);
            else append(ev.t || "");
          } catch (e) { /* ignore partial */ }
        }
      }
    } catch (e) {
      append("Sorry, I couldn't connect. Please call or WhatsApp +1 368-886-0978.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-assistant-toggle"
        aria-label={open ? "Close assistant" : "Open AI assistant"}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.9, type: "spring", stiffness: 200, damping: 16 }}
        whileHover={{ scale: 1.08 }}
        className="fixed bottom-[88px] right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37] text-[#07040D] shadow-[0_10px_40px_rgba(212,175,55,0.4)] sm:bottom-[104px] sm:right-7"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-assistant-panel"
            data-lenis-prevent
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[156px] right-3 z-50 flex h-[min(560px,calc(100svh-180px))] w-[calc(100vw-24px)] max-w-[380px] flex-col overflow-hidden border border-[#D4AF37]/35 bg-[#0B0716]/95 shadow-2xl backdrop-blur-xl sm:bottom-[172px] sm:right-7"
          >
            <div className="flex items-center gap-3 border-b border-[#D4AF37]/20 px-4 py-3">
              <LogoMark className="h-9 w-9" />
              <div>
                <p className="font-display text-lg leading-none text-white">Celestial Assistant</p>
                <p className="mt-1 text-[11px] text-slate-400">AI helper · answers may not be perfect</p>
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
              {messages.map((m, i) => (m.role === "booking" ? <BookingCard key={i} b={m.booking} /> : (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <p data-testid={`chat-message-${m.role}-${i}`}
                    className={`max-w-[85%] whitespace-pre-wrap px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-2xl rounded-br-sm bg-[#D4AF37] text-[#07040D]" : "rounded-2xl rounded-bl-sm border border-[#D4AF37]/15 bg-[#150C28] text-slate-100"}`}>
                    {visible(m.content) ? linkify(visible(m.content)) : <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />}
                  </p>
                </div>
              )))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={s} onClick={() => send(s)} data-testid={`chat-suggestion-${i}`}
                      className="rounded-full border border-[#D4AF37]/30 px-3 py-1.5 text-xs text-slate-200 hover:border-[#D4AF37]">{s}</button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 border-t border-[#D4AF37]/20 p-3">
              <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000} placeholder="Ask a question…"
                data-testid="chat-input" aria-label="Message" className="field !py-2.5" />
              <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-button" aria-label="Send"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#D4AF37] text-[#07040D] disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
